import './styles/base.sss';
import './styles/fonts.sss';
import './styles/reset.sss';

import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Cookies from 'js-cookie';
import { setDefaultLang } from 'ttag';

import ErrorBoundary from '@ui/ErrorBoundary';
import ToastContainer from '@components/ToastContainer';
import { defaultLang } from '@utils/i18n';
import { useRegion } from '@hook/useRegion';
import { useUserSubscriptionInfo } from '@hook/useUserSubscriptionInfo';
import { useFlagsWithLoadingState } from '@hook/useFlags';
import { useUTMLabels } from '@hook/useUTMLabels';
import { loginTrigger } from '@utils/analytics';
import { SOCIAL_LOGIN_COOKIE } from '@constants';
import { useGTagConfigEffect } from '@hook/useGTagConfigEffect';
import { useClerk } from '@hook/useClerk';
import { useViewer } from '@hook/useViewer';
import { getTestFlagsString, useIsClient } from '@utils/common';
import { useAppContext } from '@contexts/AppContext';
import RoutesOutlet from '@outlet/routes';
import { GoogleOneTapAuthContainer } from '@containers/GoogleOneTapAuth';
import { useIdleGtag } from '@hook/useIdleGTag';

import Routes from './routes';

setDefaultLang(defaultLang);
dayjs.extend(utc);

export const BaseApp: React.FC = () => {
  const location = useLocation();

  const isClient = useIsClient();

  const { isOutlet } = useAppContext();

  // Preload viewer
  const { viewer, loading: viewerLoading } = useViewer();
  // Preload region info
  useRegion();
  // Preload user subscription info
  useUserSubscriptionInfo();
  // Preload flags
  const { flags, loading: flagsLoading } = useFlagsWithLoadingState();

  // Init GTag
  useIdleGtag();
  useGTagConfigEffect();

  const {
    parseUTMLabelsFromUrl,
    parseAllUTMLabels,
    saveUTMLabels,
    saveAllUTMLabels,
  } = useUTMLabels();

  const {
    initClerk,
    sendClerkPageViewEvent,
  } = useClerk();

  useEffect(() => {
    const utmLabels = parseUTMLabelsFromUrl();
    const allUtmLabels = parseAllUTMLabels();

    saveAllUTMLabels(allUtmLabels);

    if (utmLabels) {
      saveUTMLabels(utmLabels);
    }

  }, [parseAllUTMLabels, parseUTMLabelsFromUrl, saveAllUTMLabels, saveUTMLabels]);

  useEffect(
    () => {
      if (Cookies.get(SOCIAL_LOGIN_COOKIE)) {
        Cookies.remove(SOCIAL_LOGIN_COOKIE);
        loginTrigger();
      }
    },
    [],
  );

  useEffect(() => {
    if (window.BASE_TEMPLATE_SOURCE === 'ssr') {
      if (!viewerLoading && !flagsLoading) {
        initClerk({
          userId: viewer?.id,
          abTests: getTestFlagsString(flags),
        });
      }
    }
  }, [flags, flagsLoading, viewerLoading, viewer, initClerk]);
  

  useEffect(() => {
    if (!viewerLoading && !flagsLoading) {
      sendClerkPageViewEvent();
    }
  }, [
    flagsLoading,
    location.pathname,
    location.search,
    sendClerkPageViewEvent,
    viewerLoading,
  ]);

  return (
    <React.StrictMode>
      <ErrorBoundary>
        {!!isClient && (
          <ToastContainer />
        )}

        <GoogleOneTapAuthContainer />
        {isOutlet ? <RoutesOutlet /> : <Routes />}
      </ErrorBoundary>
    </React.StrictMode>
  );
};

export default BaseApp;
